import {
  Divider,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Switch,
  Typography,
} from 'antd'
import { mergeNiiCalculatorAssumptionsForSite } from 'features/drilldown/panels/impl/niiRoi/niiRoiCalculatorModel'
import type {
  LibraTicketGroup,
  SiteRoiAssumptionsValue,
} from 'features/libra/ticket/libraTicketsApi'
import {
  TAR_ROI_GLOBAL_STORAGE_KEY,
  TAR_ROI_GROUP_DATA_KEY,
  useUpdateLibraTicketGroup,
} from 'features/libra/ticket/libraTicketsApi'
import { useGeckoPermissions } from 'features/permissions'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
} from 'types'

const { Text, Title } = Typography

type RoiFormShape = Omit<
  SiteRoiAssumptionsValue,
  'hseIncidentProbPerPersonPerTar'
> & {
  siteId: string
  hseIncidentProbPercent: number | undefined
}

type NiiRoiAssumptionsSettingsModalProps = {
  open: boolean
  onClose: () => void
  group: LibraTicketGroup
  siteOptions: ReadonlyArray<{ value: string; label: string }>
  selectedSiteId: string | undefined
  onSaved: () => void
}

export const NiiRoiAssumptionsSettingsModal: FC<
  NiiRoiAssumptionsSettingsModalProps
> = ({ open, onClose, group, siteOptions, selectedSiteId, onSaved }) => {
  const { hasPermissions } = useGeckoPermissions()
  const canEditRoi = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_LIBRA_EXECUTIVE,
  ])
  const doUpdate = useUpdateLibraTicketGroup()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<RoiFormShape>()

  const defaultSite = useMemo(() => {
    if (selectedSiteId) return selectedSiteId
    return siteOptions[0]?.value
  }, [selectedSiteId, siteOptions])

  const loadFormForSite = useCallback(
    (site: string) => {
      const tar = group[TAR_ROI_GROUP_DATA_KEY] ?? {}
      const globalRaw = tar[TAR_ROI_GLOBAL_STORAGE_KEY]
      const siteRaw = tar[site]
      const a = mergeNiiCalculatorAssumptionsForSite(globalRaw, siteRaw)
      form.setFieldsValue({
        siteId: site,
        traditionalInspectionTeamPerAsset: a.traditionalInspectionTeamPerAsset,
        equipmentPrepMobDemobAedPerAsset: a.equipmentPrepMobDemobAedPerAsset,
        traditionalInspectionAedPerAsset: a.traditionalInspectionAedPerAsset,
        niiInspectionAedPerAsset: a.niiInspectionAedPerAsset,
        scaffoldingAedPerAsset: a.scaffoldingAedPerAsset,
        niiInspectionRateUsdPerM2: a.niiInspectionRateUsdPerM2,
        internalInspectionRateUsdPerM2: a.internalInspectionRateUsdPerM2,
        fxRateAedPerUsd: a.fxRateAedPerUsd,
        hseIncidentProbPercent: a.hseIncidentProbPerPersonPerTar * 100,
        hseDownsideAed: a.hseDownsideAed,
        criticalPathReductionPercent: a.criticalPathReductionPercent,
        extensionProbBaselinePercent: a.extensionProbBaselinePercent,
        extensionProbWithNiiPercent: a.extensionProbWithNiiPercent,
        expectedExtensionDays: a.expectedExtensionDays,
        discoveryConfidencePercent: a.discoveryConfidencePercent,
        tarDurationDays: a.tarDurationDays,
        dailyProductionAed: a.dailyProductionAed,
        inspectionOnCriticalPath: a.inspectionOnCriticalPath,
      })
    },
    [form, group]
  )

  useEffect(() => {
    if (!open) return
    const site = defaultSite
    if (!site) return
    loadFormForSite(site)
  }, [open, defaultSite, loadFormForSite])

  const handleSiteChange = useCallback(
    (site: string) => {
      loadFormForSite(site)
    },
    [loadFormForSite]
  )

  const onOk = useCallback(async () => {
    if (!canEditRoi) {
      message.warning('You do not have permission to edit ROI settings.')
      return
    }
    const v = await form.validateFields()
    const siteId = v.siteId
    if (!siteId) {
      message.error('Select a site.')
      return
    }
    setSaving(true)
    try {
      const prev = group[TAR_ROI_GROUP_DATA_KEY] ?? {}
      // Campaign-wide assumptions shared across every turnaround.
      const globalPayload: SiteRoiAssumptionsValue = {
        traditionalInspectionTeamPerAsset: v.traditionalInspectionTeamPerAsset,
        equipmentPrepMobDemobAedPerAsset: v.equipmentPrepMobDemobAedPerAsset,
        traditionalInspectionAedPerAsset: v.traditionalInspectionAedPerAsset,
        niiInspectionAedPerAsset: v.niiInspectionAedPerAsset,
        scaffoldingAedPerAsset: v.scaffoldingAedPerAsset,
        niiInspectionRateUsdPerM2: v.niiInspectionRateUsdPerM2,
        internalInspectionRateUsdPerM2: v.internalInspectionRateUsdPerM2,
        fxRateAedPerUsd: v.fxRateAedPerUsd,
        hseIncidentProbPerPersonPerTar:
          v.hseIncidentProbPercent === undefined
            ? undefined
            : v.hseIncidentProbPercent / 100,
        hseDownsideAed: v.hseDownsideAed,
        criticalPathReductionPercent: v.criticalPathReductionPercent,
        extensionProbBaselinePercent: v.extensionProbBaselinePercent,
        extensionProbWithNiiPercent: v.extensionProbWithNiiPercent,
        expectedExtensionDays: v.expectedExtensionDays,
        discoveryConfidencePercent: v.discoveryConfidencePercent,
      }
      // Turnaround-specific inputs saved on the selected site row.
      const tarPayload: SiteRoiAssumptionsValue = {
        tarDurationDays: v.tarDurationDays,
        dailyProductionAed: v.dailyProductionAed,
        inspectionOnCriticalPath: v.inspectionOnCriticalPath,
      }
      await doUpdate({
        group,
        next: {
          title: group.title,
          totalAssets: group.totalAssets,
          owners: group.owners,
          [TAR_ROI_GROUP_DATA_KEY]: {
            ...prev,
            [TAR_ROI_GLOBAL_STORAGE_KEY]: {
              ...(prev[TAR_ROI_GLOBAL_STORAGE_KEY] ?? {}),
              ...globalPayload,
            },
            // Replace the site row with only turnaround-specific inputs. This
            // also drops any legacy campaign-wide keys previously persisted
            // here (before ROI assumptions were split into global vs
            // turnaround); left in place they would shadow the global row in
            // mergeNiiCalculatorAssumptionsForSite, which reads the site row
            // first.
            [siteId]: tarPayload,
          },
        },
      })
      message.success('ROI assumptions saved')
      onSaved()
      onClose()
    } catch (e) {
      console.error(e)
      message.error('Could not save ROI assumptions')
    } finally {
      setSaving(false)
    }
  }, [form, doUpdate, group, canEditRoi, onClose, onSaved])

  if (siteOptions.length === 0) {
    return (
      <Modal
        title='ROI assumptions'
        open={open}
        onCancel={onClose}
        footer={null}>
        <Text type='secondary'>Add tickets with a site tag first.</Text>
      </Modal>
    )
  }

  return (
    <Modal
      title='ROI assumptions'
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText='Save'
      okButtonProps={{ disabled: !canEditRoi, loading: saving }}
      width={560}>
      <Form form={form} layout='vertical' size='small' disabled={!canEditRoi}>
        <Title level={5} style={{ marginTop: 0 }}>
          Global assumptions
        </Title>
        <Text type='secondary'>
          Saved once for the campaign and shared across all sites. Asset cost
          values act as defaults and are overridden by per-asset contract costs
          when present.
        </Text>
        <Form.Item
          name='equipmentPrepMobDemobAedPerAsset'
          label='Equipment prep / mobilization / demobilization (AED / asset)'
          style={{ marginTop: 12 }}>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='scaffoldingAedPerAsset'
          label='Scaffolding (AED / asset)'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='traditionalInspectionAedPerAsset'
          label='Traditional inspection fallback (AED / asset)'
          tooltip='Flat cost used only when an asset has no zone areas to price per-m².'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='niiInspectionAedPerAsset'
          label='NII inspection fallback (AED / asset)'
          tooltip='Flat cost used only when an asset has no blended NII area to price per-ft².'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>

        <Divider style={{ margin: '16px 0' }} />

        <Title level={5} style={{ marginBottom: 4 }}>
          Inspection rates
        </Title>
        <Text type='secondary'>
          Rate-based inspection cost is preferred when an asset has zone areas.
        </Text>
        <Form.Item
          name='niiInspectionRateUsdPerM2'
          label='NII inspection rate ($ / m²)'
          style={{ marginTop: 12 }}
          tooltip='Applied to the asset’s blended NII (coverage) area: area × rate × FX.'>
          <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='internalInspectionRateUsdPerM2'
          label='Internal inspection rate ($ / m²)'
          tooltip='Applied to the asset’s approximate vessel size: area × rate × FX.'>
          <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='fxRateAedPerUsd' label='FX rate (AED per USD)'>
          <InputNumber min={0} step={0.0001} style={{ width: '100%' }} />
        </Form.Item>

        <Divider style={{ margin: '16px 0' }} />

        <Title level={5} style={{ marginBottom: 4 }}>
          HSE &amp; schedule
        </Title>
        <Form.Item
          name='traditionalInspectionTeamPerAsset'
          label='Traditional inspection team size (per asset)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='hseIncidentProbPercent'
          label='HSE incident probability (per person per TAR)'>
          <InputNumber
            min={0}
            step={0.01}
            suffix='%'
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name='hseDownsideAed'
          label='HSE downside cost per incident (AED)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='criticalPathReductionPercent'
          label='Schedule reduction factor (when on critical path) (%)'>
          <InputNumber min={0} max={100} style={{ width: '100%' }} />
        </Form.Item>

        <Divider style={{ margin: '16px 0' }} />

        <Title level={5} style={{ marginBottom: 4 }}>
          Extension avoidance
        </Title>
        <Text type='secondary'>
          Reduced probability of the TAR running long due to unexpected
          findings. Applied per turnaround in NII scope.
        </Text>
        <Form.Item
          name='extensionProbBaselinePercent'
          label='Extension probability — baseline (%)'
          style={{ marginTop: 12 }}
          tooltip='Probability the TAR extends under traditional inspection.'>
          <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='extensionProbWithNiiPercent'
          label='Extension probability — with NII (%)'
          tooltip='Reduced probability of extension with NII pre-TAR discovery.'>
          <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='expectedExtensionDays'
          label='Expected extension days'
          tooltip='Expected length of a TAR extension when it occurs.'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='discoveryConfidencePercent'
          label='Discovery confidence factor (%)'
          tooltip='Confidence NII findings are discovered early enough to avoid the extension.'>
          <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
        </Form.Item>

        <Divider style={{ margin: '16px 0' }} />

        <Title level={5} style={{ marginBottom: 4 }}>
          Turnaround inputs
        </Title>
        <Text type='secondary'>
          These values vary per turnaround. Select a site to view or edit its
          inputs.
        </Text>
        <Form.Item
          name='siteId'
          label='Site'
          rules={[{ required: true }]}
          style={{ marginTop: 12 }}>
          <Select
            options={[...siteOptions]}
            onChange={handleSiteChange}
            showSearch
            optionFilterProp='label'
          />
        </Form.Item>
        <Form.Item name='tarDurationDays' label='TAR duration (days)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='dailyProductionAed'
          label='Daily production value (AED / day)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='inspectionOnCriticalPath'
          label='Inspection on critical path'
          valuePropName='checked'>
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
